import React from 'react';
import Text from '@amzn/meridian/text';
import DisplaySubMetric from './DisplaySubMetric';
import Row from '@amzn/meridian/row';
import { FormattedMessage } from 'react-intl';
import { DisplaySubMetricsProps } from '../types';
import {
  ConcessionRateDataCheck,
  SUBMETRIC_FONT_SIZE,
  SUBMETRIC_ROW_WIDTHS,
} from '../helper';
import { ControlledExpander } from './Util/ControlledExpander';
import Column from '@amzn/meridian/column';
import NoSubMetric from './Util/NoSubMetric';

const DisplayConcessionRateSubMetrics: React.FC<DisplaySubMetricsProps> = ({
  metrics,
}: DisplaySubMetricsProps): React.ReactElement => {
  if (!metrics.CONCESSION_RATE?.submetrics) {
    return <></>;
  }
  if (ConcessionRateDataCheck(metrics.CONCESSION_RATE?.submetrics!)) {
    return <NoSubMetric />;
  }
  const { promoteGCIssuedCount, oopExceptions, concessionIssuedPerContact } =
    metrics.CONCESSION_RATE?.submetrics!;

  return (
    <Column spacing='none' width='100%'>
      {concessionIssuedPerContact && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_concessionIssuedPerContact'
                  defaultMessage='CipC (Concession Issued per Contact)'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_concessionIssuedPerContact_def'
                defaultMessage='Average concession count per resolved contact for which the associate issued a concession.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={concessionIssuedPerContact.datapoint}
            isBold
          />
          <DisplaySubMetric
            value={concessionIssuedPerContact.networkAverage}
            datapointNull={concessionIssuedPerContact.datapoint === null}
          />
        </Row>
      )}

      {oopExceptions && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_oopExceptions'
                  defaultMessage='OOP/OOW Exceptions'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_oopExceptions_def'
                defaultMessage='Sum of Concession count issued as Out of Policy by the associate.'
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={Math.round(parseFloat(oopExceptions.datapoint!)).toString()}
            isBold
          />
          <DisplaySubMetric
            value={
              oopExceptions.networkAverage === '--'
                ? oopExceptions.networkAverage
                : Math.round(
                    parseFloat(oopExceptions.networkAverage!)
                  ).toString()
            }
            datapointNull={oopExceptions.datapoint === null}
          />
        </Row>
      )}

      {promoteGCIssuedCount && (
        <Row
          alignmentVertical='top'
          width='100%'
          widths={SUBMETRIC_ROW_WIDTHS}
          spacing='none'
          spacingInset='none none 200'
        >
          <ControlledExpander
            title={
              <Text type={SUBMETRIC_FONT_SIZE}>
                <FormattedMessage
                  id='cs_perfportal_lighthouse_promoGCIssuedCount'
                  defaultMessage='Promo GC Issued Count'
                />
              </Text>
            }
            type='list'
          >
            <Text type={SUBMETRIC_FONT_SIZE}>
              <FormattedMessage
                id='cs_perfportal_lighthouse_promoGCIssuedCount_def'
                defaultMessage={`Amount of Promo GC (excludes Device Out of Warranty) or Gift Cards issued to the customer's account by the associate.`}
              />
            </Text>
          </ControlledExpander>
          <DisplaySubMetric
            value={Math.round(
              parseFloat(promoteGCIssuedCount.datapoint!)
            ).toString()}
            isBold
          />
          <DisplaySubMetric
            value={
              promoteGCIssuedCount.networkAverage === '--'
                ? '--'
                : Math.round(
                    parseFloat(promoteGCIssuedCount.networkAverage!)
                  ).toString()
            }
            datapointNull={promoteGCIssuedCount.datapoint === null}
          />
        </Row>
      )}
    </Column>
  );
};

export default DisplayConcessionRateSubMetrics;
